import { useDispatch, useSelector } from "react-redux";

import { WizardLegend } from "@similarweb/ui-components";

import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import {
    StyledBannerWrapper,
    StyledCrmEnrichmentWizardContainer,
    StyledCrmEnrichmentWizardContent,
    StyledCrmEnrichmentWizardLegend,
    StyledFooterWrapper,
} from "../../../components/styles";
import CrmEnrichmentWizardNavButtons from "../../../components/wizard/CrmEnrichmentWizardNavButtons";
import CrmEnrichmentFieldsMappingWizardStepContainer from "../../../containers/wizard/CrmEnrichmentFieldsMappingWizardStepContainer";
import CrmEnrichmentJobFiltersWizardStepContainer from "../../../containers/wizard/CrmEnrichmentJobFiltersWizardStepContainer";
import CrmEnrichmentJobNameModalContainer from "../../../containers/wizard/CrmEnrichmentJobNameModalContainer";
import CrmEnrichmentJobSettingsWizardStepContainer from "../../../containers/wizard/CrmEnrichmentJobSettingsWizardStepContainer";
import CrmEnrichmentJobTypeWizardStepContainer from "../../../containers/wizard/CrmEnrichmentJobTypeWizardStepContainer";
import { useCrmEnrichmentContext } from "../../../contexts/CrmEnrichmentContext";
import useStatefullWizardStepsValidation from "../../../hooks/wizard/useStatefullWizardStepsValidation";
import useWizardSteps from "../../../hooks/wizard/useWizardSteps";
import crmEnrichmentTrackingService from "../../../services/tracking/trackingService";
import { resetWizardFilters, setMappings, setRenameModalOpen } from "../../../store/action-creators";
import { selectSelectedJobObject } from "../../../store/selectors";
import type { CrmEnrichmentJobObject } from "../../../types/types";
import { CrmEnrichmentWizardSteps } from "../../../types/types";
import { getDefaultMappings } from "../../../utils/defaultMappings";
import { ENRICHMENT_OBJECT_TO_EXPORT_NAME } from "../../../utils/enrichmentObjectToExportName";
import { isCrmConfigurationError } from "../../../utils/errors";
import SfConfigurationErrorBanner from "../components/SfConfigurationErrorBanner/SfConfigurationErrorBanner";
import { SF_JOB_OBJECT_OPTIONS } from "../constants/enrichment-job";
import useSfEnrichmentJob from "../hooks/useSfEnrichmentJob";
import useSfEnrichmentJobCreateMutation from "../query/mutations/useSfEnrichmentJobCreateMutation";
import { selectSfSettings } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforce/exportSelectors";

import { selectSfColumnsBySelectedJobObject, selectSfCrmFieldOptions } from "../store/selectors";

import SfJobReviewWizardStepContainer from "./SfJobReviewWizardStepContainer";

const SfJobWizardContainer = () => {
    const dispatch = useDispatch();

    const { crmProvider } = useCrmEnrichmentContext();

    const columns = useSelector(selectSfColumnsBySelectedJobObject);
    const crmFieldOptions = useSelector(selectSfCrmFieldOptions);
    const selectedJobObject = useSelector(selectSelectedJobObject);
    const sfSettings = useSelector(selectSfSettings);

    const createJobMutation = useSfEnrichmentJobCreateMutation();

    const { createJobDTO } = useSfEnrichmentJob({ columns });

    const { activePageIndex, legendItems, moveToStep, goNextStep, goPrevStep } = useWizardSteps(crmProvider);
    const { isNextButtonDisabled, isAllPreviousStepsValid } = useStatefullWizardStepsValidation();

    const handleGoNextStep = () => {
        crmEnrichmentTrackingService.wizard.trackClickNext(activePageIndex, crmProvider);

        if (activePageIndex === CrmEnrichmentWizardSteps.ReviewStep) {
            dispatch(setRenameModalOpen(true));
        } else {
            goNextStep();
        }
    };

    const handleMoveToStep = (index: CrmEnrichmentWizardSteps) => {
        if (index <= activePageIndex || isAllPreviousStepsValid(index)) {
            moveToStep(index);
        }
    };

    const handleSaveJob = (name: string) => {
        crmEnrichmentTrackingService.wizard.trackFinish(name, crmProvider);

        createJobMutation.reset();
        createJobMutation.mutate(createJobDTO(), {
            onSuccess: () => {
                dispatch(setRenameModalOpen(false));

                void siNavigator.goToCrmManagementPage({ crm: crmProvider });
            },
            onError: () => dispatch(setRenameModalOpen(false)),
        });
    };

    const handleResetFiltersAndMappings = (newObject?: CrmEnrichmentJobObject) => {
        dispatch(resetWizardFilters());
        const object = newObject || (selectedJobObject as CrmEnrichmentJobObject) || "company";
        const exportSettings = sfSettings[ENRICHMENT_OBJECT_TO_EXPORT_NAME[object]];
        dispatch(setMappings(getDefaultMappings(crmProvider, object, exportSettings)));
    };

    const renderStepContent = () => {
        switch (activePageIndex) {
            case CrmEnrichmentWizardSteps.TypeStep:
                return <CrmEnrichmentJobTypeWizardStepContainer />;

            case CrmEnrichmentWizardSteps.SettingsStep:
                return (
                    <CrmEnrichmentJobSettingsWizardStepContainer
                        jobObjectOptions={SF_JOB_OBJECT_OPTIONS}
                        onSelectJobObject={handleResetFiltersAndMappings}
                    />
                );

            case CrmEnrichmentWizardSteps.FiltersStep:
                return (
                    <CrmEnrichmentJobFiltersWizardStepContainer
                        columns={columns}
                        crmFieldOptions={crmFieldOptions}
                    />
                );

            case CrmEnrichmentWizardSteps.FieldsMappingStep:
                return (
                    <CrmEnrichmentFieldsMappingWizardStepContainer
                        columns={columns}
                        isColumnsLoading={false}
                    />
                );

            case CrmEnrichmentWizardSteps.ReviewStep:
                return <SfJobReviewWizardStepContainer onMoveToStep={handleMoveToStep} />;

            default:
                return null;
        }
    };

    return (
        <StyledCrmEnrichmentWizardContainer>
            <StyledCrmEnrichmentWizardLegend>
                <WizardLegend
                    items={legendItems}
                    onItemClickFunc={handleMoveToStep}
                />
            </StyledCrmEnrichmentWizardLegend>

            {isCrmConfigurationError(createJobMutation.error) ? (
                <StyledBannerWrapper>
                    {/**
                        @note
                        Using `error?.name` due to `getResponseError` (app/services/fetchService/utils/getResponseError.ts) implementation
                    */}
                    <SfConfigurationErrorBanner errorMessage={createJobMutation.error?.name} />
                </StyledBannerWrapper>
            ) : null}

            <StyledCrmEnrichmentWizardContent
                $isFillHeight={activePageIndex === CrmEnrichmentWizardSteps.FieldsMappingStep}
            >
                {renderStepContent()}
            </StyledCrmEnrichmentWizardContent>

            <StyledFooterWrapper>
                <CrmEnrichmentWizardNavButtons
                    isNextDisabled={isNextButtonDisabled(activePageIndex)}
                    isLoading={createJobMutation.isPending}
                    activePageIndex={activePageIndex}
                    numberOfPages={legendItems.length}
                    onBackStepClick={goPrevStep}
                    onNextStepClick={handleGoNextStep}
                />
            </StyledFooterWrapper>

            <CrmEnrichmentJobNameModalContainer
                isSubmitLoading={createJobMutation.isPending}
                onSaveName={handleSaveJob}
            />
        </StyledCrmEnrichmentWizardContainer>
    );
};

export default SfJobWizardContainer;
